import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Combobox from "@/components/Combobox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import StatusBadge from "@/components/StatusBadge";
import ImportFobPricingTab from "@/pages/ImportFobPricingTab";
import { usd, shortDate } from "@/lib/format";
import { toast } from "sonner";
import {
  Search, Calculator, TrendingUp, TrendingDown, Minus, Zap, FileText, Ship, Plane,
  AlertTriangle, Activity, MapPin, Anchor, ArrowUpRight, ArrowDownRight, Plus, Trash2,
  Truck, Train, Package, Boxes, Shield, FileCheck, Star, Clock, ChevronRight, Phone, Upload,
  Building2, Home, Warehouse, Container, Info,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine,
} from "recharts";

const MODES = [
  { value: "OCEAN_FCL", label: "Ocean FCL", icon: Ship },
  { value: "OCEAN_LCL", label: "Ocean LCL", icon: Ship },
  { value: "AIR", label: "Air Freight", icon: Plane },
];

export default function Procurement() {
  return (
    <div className="space-y-6 animate-slide-up" data-testid="procurement-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Procurement &amp; Rates</h1>
          <p className="text-sm text-zinc-500 mt-1">Australia-focused freight intelligence. Instant quotes, market trends, contract library, landed cost &amp; AU compliance.</p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-[#E6EBF6] text-[#002FA7] border border-[#A7B4D9]">AU Trade Edition</span>
      </div>

      <Tabs defaultValue="instant" className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 p-0 h-auto w-full justify-start overflow-x-auto">
          {[
            { v: "instant", l: "Instant Quote", t: "tab-instant" },
            { v: "fob", l: "Import FOB Pricing", t: "tab-fob" },
            { v: "trend", l: "Rate Benchmark & Trend", t: "tab-trend" },
            { v: "contracts", l: "Contract Library", t: "tab-contracts" },
            { v: "landed", l: "Landed Cost (AU)", t: "tab-landed" },
            { v: "au", l: "AU Trade Intelligence", t: "tab-au" },
          ].map(t => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              data-testid={t.t}
              className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-2.5 whitespace-nowrap"
            >{t.l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="instant" className="mt-6"><InstantQuoteTab /></TabsContent>
        <TabsContent value="fob" className="mt-6"><ImportFobPricingTab /></TabsContent>
        <TabsContent value="trend" className="mt-6"><TrendTab /></TabsContent>
        <TabsContent value="contracts" className="mt-6"><ContractsTab /></TabsContent>
        <TabsContent value="landed" className="mt-6"><LandedCostTab /></TabsContent>
        <TabsContent value="au" className="mt-6"><AuIntelTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Instant Quote Tab ============
function InstantQuoteTab() {
  const [lanes, setLanes] = useState([]);
  const [form, setForm] = useState({
    mode: "OCEAN_FCL", origin_port: "CNSHA", destination_port: "AUSYD",
    container_type: "40HC", cbm: 5, weight_kg: 1000, incoterm: "FOB", commodity: "General Cargo",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { api.get("/procurement/lanes").then(r => setLanes(r.data)); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/procurement/instant-quote", form);
      setResult(r.data);
    } catch { toast.error("Quote failed"); }
    setBusy(false);
  };

  const reset = () => {
    setForm({
      mode: "OCEAN_FCL", origin_port: "", destination_port: "",
      container_type: "40HC", cbm: 5, weight_kg: 1000, incoterm: "FOB", commodity: "General Cargo",
    });
    setResult(null);
    setExpanded(null);
  };

  // Clear stale result whenever the user edits any input.
  useEffect(() => { setResult(null); setExpanded(null); }, [form]);

  return (
    <div className="space-y-6">
      {/* Cargo intake */}
      <div className="bg-white border border-zinc-200 p-5">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="data-label">Mode</Label>
            <Select value={form.mode} onValueChange={v => setForm({ ...form, mode: v })}>
              <SelectTrigger data-testid="iq-mode" className="rounded-none mt-1.5 h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="data-label">Origin</Label>
            <Combobox
              testId="iq-origin"
              value={form.origin_port}
              onChange={(v) => setForm({ ...form, origin_port: v })}
              options={[...new Map(lanes.map(l => [l.origin_port, l])).values()].map(l => ({ value: l.origin_port, label: `${l.origin_name} (${l.origin_port})` }))}
              placeholder="Select origin..."
              emptyText="No matching origins."
            />
          </div>
          <div className="col-span-2">
            <Label className="data-label">Destination (AU)</Label>
            <Combobox
              testId="iq-dest"
              value={form.destination_port}
              onChange={(v) => setForm({ ...form, destination_port: v })}
              options={[...new Map(lanes.map(l => [l.destination_port, l])).values()].map(l => ({ value: l.destination_port, label: `${l.destination_name} (${l.destination_port})` }))}
              placeholder="Select destination..."
              emptyText="No matching destinations."
            />
          </div>
          <div>
            <Label className="data-label">Incoterm</Label>
            <Select value={form.incoterm} onValueChange={v => setForm({ ...form, incoterm: v })}>
              <SelectTrigger data-testid="iq-incoterm" className="rounded-none mt-1.5 h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{["FOB","EXW","FCA","CIF","CFR","DAP","DDP"].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {form.mode === "OCEAN_FCL" && (
            <div>
              <Label className="data-label">Container</Label>
              <Combobox
                testId="iq-ctype"
                value={form.container_type}
                onChange={(v) => setForm({ ...form, container_type: v })}
                options={["20GP","40GP","40HC","45HC","20RF","40RF"]}
                placeholder="Container..."
              />
            </div>
          )}
          {form.mode === "OCEAN_LCL" && (
            <>
              <div><Label className="data-label">Volume (CBM)</Label><Input data-testid="iq-cbm" type="number" value={form.cbm} onChange={e => setForm({ ...form, cbm: Number(e.target.value) })} className="rounded-none mt-1.5 h-10 metric" /></div>
              <div><Label className="data-label">Weight (kg)</Label><Input data-testid="iq-weight" type="number" value={form.weight_kg} onChange={e => setForm({ ...form, weight_kg: Number(e.target.value) })} className="rounded-none mt-1.5 h-10 metric" /></div>
            </>
          )}
          {form.mode === "AIR" && (
            <>
              <div><Label className="data-label">Weight (kg)</Label><Input data-testid="iq-weight" type="number" value={form.weight_kg} onChange={e => setForm({ ...form, weight_kg: Number(e.target.value) })} className="rounded-none mt-1.5 h-10 metric" /></div>
              <div><Label className="data-label">Volume (CBM)</Label><Input data-testid="iq-cbm" type="number" value={form.cbm} onChange={e => setForm({ ...form, cbm: Number(e.target.value) })} className="rounded-none mt-1.5 h-10 metric" /></div>
            </>
          )}

          <div className={form.mode === "OCEAN_FCL" ? "col-span-2 md:col-span-3" : "col-span-2"}>
            <Label className="data-label">Commodity</Label>
            <Input data-testid="iq-commodity" value={form.commodity} onChange={e => setForm({ ...form, commodity: e.target.value })} className="rounded-none mt-1.5 h-10" />
          </div>
          <Button data-testid="iq-search" onClick={run} disabled={busy || !form.origin_port || !form.destination_port} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10">
            <Zap className="w-4 h-4 mr-2" />{busy ? "..." : "Get quotes"}
          </Button>
          <Button data-testid="iq-reset" onClick={reset} variant="outline" className="rounded-none h-10 border-zinc-300 hover:border-[#991B1B] hover:text-[#991B1B]">Reset</Button>
        </div>
      </div>

      {!result && !busy && (
        <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="iq-empty">
          <Ship className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Enter your cargo details above and click <span className="font-semibold text-zinc-700">Get quotes</span> to compare forwarders.</p>
        </div>
      )}

      {result && (
        <>
          {/* Benchmark band */}
          <div className="bg-white border border-zinc-200 p-5" data-testid="iq-benchmark">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="data-label text-[#002FA7]">{result.lane}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{result.mode.replace("_", " ")} · {result.incoterm} · transit ~{result.transit_typical_days}d typical · {result.quotes.length} forwarders</div>
              </div>
              <div className="flex gap-2 text-[10px] uppercase tracking-[0.12em]">
                {result.bmsb_season_active && <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#B45309] border border-[#FCD34D] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />BMSB season</span>}
                {result.peak_season_active && <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#B45309] border border-[#FCD34D]">Peak season</span>}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
              <div className="bg-white p-4"><div className="data-label text-[#166534]">Cheapest</div><div className="metric text-2xl mt-1">{usd(result.benchmark.market_low)}</div></div>
              <div className="bg-white p-4 border-l-[3px] border-l-[#002FA7]"><div className="data-label text-[#002FA7]">Market Average</div><div className="metric text-2xl mt-1">{usd(result.benchmark.market_avg)}</div></div>
              <div className="bg-white p-4"><div className="data-label text-[#991B1B]">Most Expensive</div><div className="metric text-2xl mt-1">{usd(result.benchmark.market_high)}</div></div>
              <div className="bg-white p-4"><div className="data-label">Spread</div><div className="metric text-2xl mt-1">{result.benchmark.spread_pct}%</div></div>
            </div>
          </div>

          {/* Quotes list */}
          <div className="bg-white border border-zinc-200" data-testid="iq-quotes">
            <div className="px-5 py-4 border-b border-zinc-200">
              <h3 className="font-heading text-lg font-bold tracking-tight">Forwarder Quotes (ranked by total)</h3>
              <p className="text-xs text-zinc-500 mt-0.5">All-in price including AU destination charges (THC, AMSA, DAFF Biosecurity, wharf, cartage).</p>
            </div>
            <div className="divide-y divide-zinc-100">
              {result.quotes.map((q, idx) => {
                const cheapest = idx === 0;
                const open = expanded === idx;
                return (
                  <div key={idx} className={`px-5 py-4 hover:bg-zinc-50/60 transition-colors ${cheapest ? 'bg-[#F0FDF4]/30' : ''}`} data-testid={`quote-row-${idx}`}>
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-3">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-semibold">{q.forwarder}</span>
                          {cheapest && <span className="text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 bg-[#16A34A] text-white">Best</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{q.tier} · {q.reliability_pct}% reliable · resp {q.response_hrs}h</div>
                      </div>
                      <div className="col-span-2">
                        <div className="data-label">Transit</div>
                        <div className="metric font-medium">{q.transit_days}d</div>
                      </div>
                      <div className="col-span-2">
                        <div className="data-label">Free Time</div>
                        <div className="metric font-medium">{q.free_time_days}d</div>
                      </div>
                      <div className="col-span-2">
                        <div className="data-label">Valid Until</div>
                        <div className="metric font-medium text-xs">{shortDate(q.valid_until)}</div>
                      </div>
                      <div className="col-span-2 text-right">
                        <div className="data-label">Total {q.unit_label}</div>
                        <div className="metric text-2xl font-medium tracking-tight">{usd(q.total_usd)}</div>
                      </div>
                      <div className="col-span-1 text-right">
                        <Button
                          data-testid={`quote-toggle-${idx}`}
                          size="sm" variant="outline"
                          className="rounded-none border-zinc-300 h-9"
                          onClick={() => setExpanded(open ? null : idx)}
                        >{open ? "Hide" : "Detail"}</Button>
                      </div>
                    </div>
                    {open && (
                      <div className="mt-4 pt-4 border-t border-zinc-100 grid lg:grid-cols-2 gap-4">
                        <div>
                          <div className="data-label mb-2">Line items</div>
                          <table className="w-full text-xs">
                            <tbody>
                              {q.line_items.map(li => (
                                <tr key={li.code} className="border-b border-zinc-100">
                                  <td className="py-1.5 text-zinc-600">{li.label} <span className="text-zinc-400">· {li.code}</span></td>
                                  <td className="py-1.5 text-right metric">{usd(li.amount_usd)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div>
                          <div className="data-label mb-2">Notes</div>
                          <ul className="text-xs text-zinc-700 space-y-1.5">
                            {(q.notes || []).filter(Boolean).map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#002FA7]">▸</span>{n}</li>)}
                            <li className="flex gap-2"><span className="text-[#002FA7]">▸</span>Rate valid {shortDate(q.valid_until)}. Subject to space availability.</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Trend Tab ============
function TrendTab() {
  const [lanes, setLanes] = useState([]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [trend, setTrend] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/procurement/lanes").then(r => setLanes(r.data)); }, []);

  const run = async () => {
    if (!origin || !destination) return;
    setBusy(true);
    try {
      const r = await api.get("/procurement/rate-trend", { params: { origin_port: origin, destination_port: destination } });
      setTrend(r.data);
    } catch { toast.error("Trend lookup failed"); }
    setBusy(false);
  };

  const reset = () => { setOrigin(""); setDestination(""); setTrend(null); };

  // Clear stale chart when the user changes lane inputs.
  useEffect(() => { setTrend(null); }, [origin, destination]);

  const chartData = useMemo(() => {
    if (!trend) return [];
    return [...trend.history.map(h => ({ ...h, type: "actual" })), ...trend.forecast.map(f => ({ ...f, type: "forecast" }))];
  }, [trend]);

  const TrendIcon = trend?.direction === "UP" ? ArrowUpRight : trend?.direction === "DOWN" ? ArrowDownRight : Minus;
  const trendColor = trend?.direction === "UP" ? "text-[#991B1B]" : trend?.direction === "DOWN" ? "text-[#166534]" : "text-zinc-600";

  return (
    <div className="space-y-5">
      <div className="bg-white border border-zinc-200 p-5 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="data-label">Origin</Label>
          <Combobox
            testId="trend-origin"
            value={origin}
            onChange={setOrigin}
            options={[...new Map(lanes.map(l => [l.origin_port, l])).values()].map(l => ({ value: l.origin_port, label: l.origin_name }))}
            placeholder="Select origin..."
            emptyText="No matching origins."
          />
        </div>
        <div>
          <Label className="data-label">Destination</Label>
          <Combobox
            testId="trend-dest"
            value={destination}
            onChange={setDestination}
            options={[...new Map(lanes.map(l => [l.destination_port, l])).values()].map(l => ({ value: l.destination_port, label: l.destination_name }))}
            placeholder="Select destination..."
            emptyText="No matching destinations."
          />
        </div>
        <Button data-testid="trend-search" onClick={run} disabled={busy || !origin || !destination} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10">
          <Search className="w-4 h-4 mr-2" />{busy ? "..." : "Show trend"}
        </Button>
        <Button data-testid="trend-reset" onClick={reset} variant="outline" className="rounded-none h-10 border-zinc-300 hover:border-[#991B1B] hover:text-[#991B1B]">Reset</Button>
      </div>

      {!trend && !busy && (
        <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="trend-empty">
          <TrendingUp className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Select an origin and destination, then click <span className="font-semibold text-zinc-700">Show trend</span> to see rate history and forecast.</p>
        </div>
      )}

      {trend && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            <div className="bg-white p-5">
              <div className="data-label">Current Mid Rate</div>
              <div className="metric text-3xl mt-2">{usd(trend.history[trend.history.length - 1].mid)}</div>
              <div className={`text-xs mt-2 inline-flex items-center gap-1 ${trendColor}`}><TrendIcon className="w-3.5 h-3.5" /> {trend.direction}</div>
            </div>
            <div className="bg-white p-5">
              <div className="data-label">3-Month Forecast Mid</div>
              <div className="metric text-3xl mt-2">{usd(trend.forecast[2].mid)}</div>
              <div className="text-xs mt-2 text-zinc-500">Projected by trend + seasonality</div>
            </div>
            <div className="bg-white p-5">
              <div className="data-label">Recent Volatility</div>
              <div className="metric text-3xl mt-2">{trend.volatility_pct}%</div>
              <div className="text-xs mt-2 text-zinc-500">last 3 months range</div>
            </div>
            <div className={`bg-white p-5 border-l-[3px] ${trend.direction === "UP" ? 'border-l-[#DC2626]' : trend.direction === "DOWN" ? 'border-l-[#16A34A]' : 'border-l-zinc-400'}`}>
              <div className="data-label">Recommendation</div>
              <div className="text-sm mt-2 leading-relaxed">{trend.rationale}</div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 p-5">
            <h3 className="font-heading text-lg font-bold tracking-tight mb-1">Rate History &amp; Forecast — USD per 40HC</h3>
            <p className="text-xs text-zinc-500 mb-4">18 months actual + 3 months forecast. Band = market low/high spread.</p>
            <ResponsiveContainer width="100%" height={320} data-testid="trend-chart">
              <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="bandFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#002FA7" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#002FA7" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#a1a1aa" />
                <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#a1a1aa" tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(v) => `$${v}`}
                  contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, border: "1px solid #E4E4E7", borderRadius: 0, background: "white" }}
                />
                <Area type="monotone" dataKey="high" stroke="none" fill="url(#bandFill)" />
                <Area type="monotone" dataKey="low" stroke="none" fill="white" />
                <Line type="monotone" dataKey="mid" stroke="#002FA7" strokeWidth={2} dot={false} />
                <ReferenceLine x={trend.history[trend.history.length - 1].month} stroke="#71717A" strokeDasharray="3 3" label={{ value: "today", fill: "#71717A", fontSize: 10, position: "insideTopRight" }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Contracts Tab ============
function ContractsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    carrier: "", origin_port: "CNSHA", destination_port: "AUSYD", lane: "", container_type: "40HC",
    rate_type: "Contract / NAC", total_usd: 2500, free_time_days: 14, monthly_teu_committed: 100,
    valid_from: new Date().toISOString().slice(0, 10), valid_until: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
  });

  const load = () => api.get("/procurement/contracts").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const expiring = items.filter(c => c.status === "EXPIRING").length;
  const expired = items.filter(c => c.status === "EXPIRED").length;
  const totalTeu = items.reduce((a, c) => a + (c.monthly_teu_committed || 0), 0);

  const create = async () => {
    if (!form.carrier || !form.lane) { toast.error("Carrier and lane are required"); return; }
    await api.post("/procurement/contracts", form);
    toast.success("Contract added");
    setOpen(false);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/procurement/contracts/${id}`);
    toast.success("Contract removed");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5"><div className="data-label">Active Contracts</div><div className="metric text-3xl mt-2">{items.length}</div></div>
        <div className="bg-white p-5 border-l-[3px] border-l-[#F59E0B]"><div className="data-label">Expiring ≤ 30 days</div><div className="metric text-3xl mt-2 text-[#B45309]">{expiring}</div></div>
        <div className="bg-white p-5 border-l-[3px] border-l-[#DC2626]"><div className="data-label">Expired</div><div className="metric text-3xl mt-2 text-[#991B1B]">{expired}</div></div>
        <div className="bg-white p-5"><div className="data-label">Committed TEU / month</div><div className="metric text-3xl mt-2">{totalTeu}</div></div>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold tracking-tight">Contract Library</h3>
          <Button data-testid="add-contract-btn" onClick={() => setOpen(true)} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-9"><Plus className="w-4 h-4 mr-2" />Add contract</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              {["Lane", "Carrier", "Type", "Rate", "Free Time", "Monthly TEU", "Valid Until", "Days Left", "Status", ""].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
            </tr>
          </thead>
          <tbody data-testid="contracts-tbody">
            {items.map(c => (
              <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                <td className="px-4 py-2.5">{c.lane}</td>
                <td className="px-4 py-2.5 font-medium">{c.carrier}</td>
                <td className="px-4 py-2.5"><span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-zinc-100 border border-zinc-200">{c.container_type}</span></td>
                <td className="px-4 py-2.5 metric">{usd(c.total_usd)}</td>
                <td className="px-4 py-2.5 metric">{c.free_time_days}d</td>
                <td className="px-4 py-2.5 metric">{c.monthly_teu_committed}</td>
                <td className="px-4 py-2.5 metric text-xs">{shortDate(c.valid_until)}</td>
                <td className="px-4 py-2.5 metric">
                  <span className={c.days_to_expiry < 0 ? "text-[#991B1B]" : c.days_to_expiry <= 30 ? "text-[#B45309]" : "text-zinc-700"}>
                    {c.days_to_expiry}d
                  </span>
                </td>
                <td className="px-4 py-2.5"><StatusBadge value={c.status} /></td>
                <td className="px-4 py-2.5"><Button data-testid={`del-contract-${c.id}`} size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => remove(c.id)}><Trash2 className="w-3.5 h-3.5 text-[#991B1B]" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} className="p-10 text-center text-sm text-zinc-500">No contracts yet. Add your first.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="add-contract-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Contract Rate</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">Log a NAC/Contract/FAK rate so the system can score it vs market and warn before expiry.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="data-label">Carrier</Label><Input data-testid="ct-carrier" value={form.carrier} onChange={e => setForm({ ...form, carrier: e.target.value })} className="rounded-none mt-1.5" /></div>
            <div><Label className="data-label">Lane Label</Label><Input data-testid="ct-lane" value={form.lane} onChange={e => setForm({ ...form, lane: e.target.value })} placeholder="Shanghai → Sydney" className="rounded-none mt-1.5" /></div>
            <div><Label className="data-label">Origin Port</Label><Input data-testid="ct-origin" value={form.origin_port} onChange={e => setForm({ ...form, origin_port: e.target.value })} className="rounded-none mt-1.5" /></div>
            <div><Label className="data-label">Destination Port</Label><Input data-testid="ct-dest" value={form.destination_port} onChange={e => setForm({ ...form, destination_port: e.target.value })} className="rounded-none mt-1.5" /></div>
            <div>
              <Label className="data-label">Container Type</Label>
              <Select value={form.container_type} onValueChange={v => setForm({ ...form, container_type: v })}>
                <SelectTrigger data-testid="ct-type" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["20GP","40GP","40HC","45HC"].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="data-label">Total USD</Label><Input data-testid="ct-total" type="number" value={form.total_usd} onChange={e => setForm({ ...form, total_usd: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
            <div><Label className="data-label">Free Time (d)</Label><Input data-testid="ct-free" type="number" value={form.free_time_days} onChange={e => setForm({ ...form, free_time_days: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
            <div><Label className="data-label">Monthly TEU Commit</Label><Input data-testid="ct-teu" type="number" value={form.monthly_teu_committed} onChange={e => setForm({ ...form, monthly_teu_committed: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
            <div><Label className="data-label">Valid From</Label><DatePicker testId="ct-from" value={form.valid_from} onChange={v => setForm({ ...form, valid_from: v })} /></div>
            <div><Label className="data-label">Valid Until</Label><DatePicker testId="ct-until" value={form.valid_until} onChange={v => setForm({ ...form, valid_until: v })} /></div>
          </div>
          <div className="flex justify-end mt-2"><Button data-testid="ct-save" onClick={create} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">Save contract</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Landed Cost Tab ============
function LandedCostTab() {
  const [form, setForm] = useState({
    freight_usd: 2800, insurance_usd: 0, commercial_value_usd: 18000, hs_code: "9403.60",
    usd_to_aud: 1.52, cartage_aud: 750, customs_broker_fee_aud: 280, daff_biosecurity_aud: 200, storage_aud: 0,
  });
  const [result, setResult] = useState(null);

  const run = async () => {
    const payload = { ...form, insurance_usd: form.insurance_usd || null };
    const r = await api.post("/procurement/landed-cost", payload);
    setResult(r.data);
  };
  useEffect(() => { run(); /* initial */ /* eslint-disable-next-line */ }, []);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="font-heading text-lg font-bold tracking-tight mb-1">Cargo &amp; Freight</h3>
        <p className="text-xs text-zinc-500 mb-4">USD freight + commercial value. AU GST + duty applied automatically based on HS code.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="data-label">Commercial Value (USD)</Label><Input data-testid="lc-value" type="number" value={form.commercial_value_usd} onChange={e => setForm({ ...form, commercial_value_usd: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">Freight Total (USD)</Label><Input data-testid="lc-freight" type="number" value={form.freight_usd} onChange={e => setForm({ ...form, freight_usd: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">Insurance (USD)</Label><Input data-testid="lc-ins" type="number" value={form.insurance_usd} onChange={e => setForm({ ...form, insurance_usd: Number(e.target.value) })} placeholder="auto: 0.5% of freight" className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">HS Code</Label><Input data-testid="lc-hs" value={form.hs_code} onChange={e => setForm({ ...form, hs_code: e.target.value })} placeholder="e.g. 9403.60" className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">USD → AUD</Label><Input data-testid="lc-fx" type="number" step="0.01" value={form.usd_to_aud} onChange={e => setForm({ ...form, usd_to_aud: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
        </div>
        <h3 className="font-heading text-base font-bold tracking-tight mt-6 mb-3">AU Destination Charges (AUD)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="data-label">Cartage to consignee</Label><Input data-testid="lc-cartage" type="number" value={form.cartage_aud} onChange={e => setForm({ ...form, cartage_aud: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">Customs Broker Fee</Label><Input data-testid="lc-broker" type="number" value={form.customs_broker_fee_aud} onChange={e => setForm({ ...form, customs_broker_fee_aud: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">DAFF Biosecurity</Label><Input data-testid="lc-biosec" type="number" value={form.daff_biosecurity_aud} onChange={e => setForm({ ...form, daff_biosecurity_aud: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
          <div><Label className="data-label">Storage</Label><Input data-testid="lc-storage" type="number" value={form.storage_aud} onChange={e => setForm({ ...form, storage_aud: Number(e.target.value) })} className="rounded-none mt-1.5 metric" /></div>
        </div>
        <Button data-testid="lc-run" onClick={run} className="rounded-none bg-[#002FA7] hover:bg-[#002480] w-full mt-5"><Calculator className="w-4 h-4 mr-2" />Calculate landed cost</Button>
      </div>

      {result && (
        <div className="bg-white border border-zinc-200 p-6" data-testid="lc-result">
          <h3 className="font-heading text-lg font-bold tracking-tight mb-1">Door-to-door Landed Cost</h3>
          <p className="text-xs text-zinc-500 mb-4">All figures in AUD. Duty {result.duty_rate_pct}% applied by HS-code prefix.</p>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">CIF Value (Freight + Insurance + Goods)</td><td className="py-2 text-right metric">A$ {result.cif_aud.toLocaleString()}</td></tr>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">Customs Duty ({result.duty_rate_pct}%)</td><td className="py-2 text-right metric">A$ {result.customs_duty_aud.toLocaleString()}</td></tr>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">GST 10% on VoTI <span className="text-xs text-zinc-400">{result.gst_applied ? '' : '(not applicable < AUD 1,000)'}</span></td><td className="py-2 text-right metric">A$ {result.gst_aud.toLocaleString()}</td></tr>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">Destination Cartage</td><td className="py-2 text-right metric">A$ {result.destination_charges_aud.cartage.toLocaleString()}</td></tr>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">Customs Broker Fee</td><td className="py-2 text-right metric">A$ {result.destination_charges_aud.customs_broker_fee.toLocaleString()}</td></tr>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">DAFF Biosecurity</td><td className="py-2 text-right metric">A$ {result.destination_charges_aud.daff_biosecurity.toLocaleString()}</td></tr>
              <tr className="border-b border-zinc-100"><td className="py-2 text-zinc-600">Storage</td><td className="py-2 text-right metric">A$ {result.destination_charges_aud.storage.toLocaleString()}</td></tr>
            </tbody>
            <tfoot>
              <tr className="bg-[#E6EBF6]"><td className="py-3 px-2 font-bold text-[#002FA7] uppercase tracking-[0.12em] text-xs">Total Landed Cost</td><td className="py-3 px-2 text-right metric font-bold text-[#002FA7] text-2xl">A$ {result.total_landed_aud.toLocaleString()}</td></tr>
            </tfoot>
          </table>
          <div className="mt-5 border-t border-zinc-200 pt-4">
            <div className="data-label mb-2">Notes</div>
            <ul className="text-xs text-zinc-600 space-y-1">
              {result.notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#002FA7]">▸</span>{n}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ AU Intelligence Tab ============
function AuIntelTab() {
  const [intel, setIntel] = useState(null);
  useEffect(() => { api.get("/procurement/au-intelligence").then(r => setIntel(r.data)); }, []);
  if (!intel) return <div className="text-sm text-zinc-500">Loading AU intelligence...</div>;

  return (
    <div className="space-y-5" data-testid="au-intel">
      {/* Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        <div className={`bg-white p-5 ${intel.bmsb_season_active ? 'border-l-[3px] border-l-[#F59E0B]' : 'border-l-[3px] border-l-[#16A34A]'}`}>
          <div className="data-label">BMSB Treatment Season</div>
          <div className="metric text-2xl mt-2 font-bold">{intel.bmsb_season_active ? "ACTIVE" : "OFF-SEASON"}</div>
          <p className="text-xs text-zinc-600 mt-2 leading-relaxed">Brown Marmorated Stink Bug. Mandatory treatment for high-risk goods from EU, USA, Italy etc. arriving onshore 1 Sep – 30 Apr.</p>
        </div>
        <div className={`bg-white p-5 ${intel.peak_season_active ? 'border-l-[3px] border-l-[#F59E0B]' : 'border-l-[3px] border-l-[#16A34A]'}`}>
          <div className="data-label">Asia–AU Peak Season</div>
          <div className="metric text-2xl mt-2 font-bold">{intel.peak_season_active ? "ACTIVE" : "OFF-PEAK"}</div>
          <p className="text-xs text-zinc-600 mt-2 leading-relaxed">PSS, equipment shortages, 1–2 week capacity backlog. Book 3–4 weeks earlier during peak.</p>
        </div>
      </div>

      {/* Live alerts */}
      {intel.alerts.length > 0 && (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200">
            <h3 className="font-heading text-lg font-bold tracking-tight">Live Trade Alerts</h3>
          </div>
          <div className="divide-y divide-zinc-100" data-testid="au-alerts">
            {intel.alerts.map((a, i) => (
              <div key={i} className="px-5 py-3 flex items-start gap-3">
                <div className={`w-1.5 h-1.5 mt-2 rounded-full ${a.severity === "CRITICAL" ? "bg-[#DC2626]" : "bg-[#F59E0B]"} animate-pulse-dot`} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{a.title}</div>
                    <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 metric">{a.code}</span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{a.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ports + Capacity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200">
            <h3 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2"><Anchor className="w-4 h-4 text-[#002FA7]" />AU Port Congestion</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">{["Port", "Berth Wait", "Status"].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}</tr>
            </thead>
            <tbody>
              {intel.port_congestion.map(p => (
                <tr key={p.port} className="border-b border-zinc-100">
                  <td className="px-4 py-3"><span className="font-medium">{p.name}</span> <span className="text-xs text-zinc-400 metric ml-1">{p.port}</span></td>
                  <td className="px-4 py-3 metric">{p.wait_days}d</td>
                  <td className="px-4 py-3"><StatusBadge value={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200">
            <h3 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2"><Activity className="w-4 h-4 text-[#002FA7]" />Capacity Outlook</h3>
          </div>
          <div className="divide-y divide-zinc-100">
            {Object.entries(intel.capacity_outlook).map(([trade, status]) => (
              <div key={trade} className="px-5 py-3 flex items-center justify-between text-sm">
                <span>{trade}</span>
                <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border ${status === "Tight" ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]' : status === "Loose" ? 'bg-[#DCFCE7] text-[#166534] border-[#86EFAC]' : 'bg-zinc-100 text-zinc-700 border-zinc-300'}`}>{status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick facts */}
      <div className="bg-white border border-zinc-200 p-5">
        <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Australian Import Quick Facts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {intel.au_quick_facts.map((f, i) => (
            <div key={i} className="bg-white p-4">
              <div className="data-label">{f.label}</div>
              <div className="text-sm mt-1 metric">{f.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
